import type { DoseSequence } from "@/types";

/**
 * How to space out the doses below, and what must not meet what.
 *
 * **This exists because "Do this, in order" was a sequencing claim with nothing
 * behind it.** The list has always been correctly ordered — acid is priority 1,
 * chlorine 30 — and nothing on screen said the order mattered, that there had
 * to be a gap, or that these two particular chemicals make chlorine gas if they
 * meet before they are diluted. A reasonable person reads a numbered list as
 * "do these now, in this sequence".
 *
 * So it sits *above* the list rather than inside it. A caution attached to one
 * card is a fact about that chemical; this is a fact about doing both, and
 * there is nowhere inside an ordered list for that to live.
 *
 * The hazard is stated at full weight and the reaction is shown. Someone who
 * wants to check the claim rather than take it on trust should be able to, and
 * an equation is a shorter way to be believed than a paragraph.
 */
export default function SequenceNote({ sequence }: { sequence: DoseSequence }) {
  const hazard = sequence.hazards[0];

  return (
    <section
      className="card mb-2 p-4"
      style={hazard ? { borderColor: "var(--color-status-high)" } : undefined}
    >
      <p
        className="text-sm font-medium"
        style={hazard ? { color: "var(--color-status-high)" } : undefined}
      >
        {sequence.headline}
      </p>

      <ol className="mt-3 space-y-2">
        {sequence.steps.map((step, index) => (
          <li key={step.chemical} className="flex gap-3">
            <span
              aria-hidden
              className="figure mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-(--color-border-subtle) text-xs text-(--color-ink-muted)"
            >
              {index + 1}
            </span>
            <div className="min-w-0">
              <p className="text-sm">
                <span className="font-medium">{step.label}</span>
                {" — "}
                <span className="text-(--color-ink-muted)">{step.when}</span>
              </p>
              {step.because && (
                <p className="mt-0.5 text-xs text-(--color-ink-faint)">{step.because}</p>
              )}
            </div>
          </li>
        ))}
      </ol>

      {hazard && (
        <>
          <p className="mt-3 text-sm text-(--color-ink-muted)">{hazard.guidance}</p>
          {/* The equation, quiet and last. It is not instruction — it is the
              receipt for the sentence at the top. */}
          <p className="figure mt-2 text-xs text-(--color-ink-faint)">{hazard.reaction}</p>
        </>
      )}
    </section>
  );
}
